import React from 'react';
import { RichTableState, TableActionGroup, TableActionOrGroup, isTableActionGroup } from '../types';
import CollapsableContent from '../CollapsableContent';
import Stack, { contentStackSpacing } from '../../Stack';
import Divider from '../../Divider';
import { TableActionButton, TableActionGroupButton } from './TableActionButton';
import { TABLE_PADDING } from '../utils';
import { useFilterShownTableActions } from './useFilterShownTableActions';
import { PRScrollView } from '../../../shared/Scroll';

export type TableActionsOwnProps = {
    state: RichTableState;
    actions: TableActionOrGroup<any>[];
    data: any[];
    getRowKey: (dataElement: any) => string;
    headerRight: React.ReactNode | null;
    renderNoActionsPlaceholder?: () => React.ReactNode;
};

export type TableActionsProps = TableActionsOwnProps;

export const ACTIONS_HEIGHT = 56;

const TableActions: React.FC<TableActionsProps> = (props) => {
    const selectedData = props.data.filter((dataElement) =>
        props.state.selectedRowKeys?.includes(props.getRowKey(dataElement)),
    );
    const { shownActions } = useFilterShownTableActions({ actions: props.actions, data: selectedData });

    if (!props.actions.length && !props.headerRight) {
        return null;
    }

    const renderTableActionGroup = (action: TableActionGroup<any>) => {
        const shownSubActions = action.actions.filter((subAction) => subAction.shouldShow(selectedData));
        return (
            <>
                <TableActionGroupButton
                    actions={shownSubActions.map((action) => ({
                        title: action.title,
                        onPress: () => action.onPress(selectedData),
                        testID: action.testID,
                        isDisabled: action.shouldDisable?.(selectedData),
                    }))}
                    testID={action.testID}
                    title={action.title}
                    icon={action.icon}
                />
                {shownSubActions.map((subAction, index) =>
                    subAction.renderAfter ? (
                        <React.Fragment key={index}>{subAction.renderAfter(selectedData)}</React.Fragment>
                    ) : null,
                )}
            </>
        );
    };

    return (
        <CollapsableContent
            isCollapsed={!shownActions.length && !props.renderNoActionsPlaceholder}
            expandedHeight={ACTIONS_HEIGHT}
            style={{ width: '100%' }}
        >
            <Stack vertical fullWidth space={contentStackSpacing}>
                <PRScrollView horizontal style={{ width: '100%' }} contentContainerStyle={{ minWidth: '100%' }}>
                    <Stack
                        horizontal
                        justify="space-between"
                        space={contentStackSpacing}
                        align="center"
                        style={{ minWidth: '100%', paddingLeft: TABLE_PADDING, paddingRight: TABLE_PADDING }}
                    >
                        <Stack horizontal justify="flex-start" space={contentStackSpacing} style={{ flex: 1 }}>
                            {shownActions?.length ? (
                                shownActions.map((action, index) => (
                                    <Stack horizontal key={`${action.title}-${index}`}>
                                        {isTableActionGroup(action) ? (
                                            renderTableActionGroup(action)
                                        ) : (
                                            <>
                                                <TableActionButton
                                                    title={action.title}
                                                    icon={action.icon}
                                                    onPress={() => action.onPress(selectedData)}
                                                    key={`${action.title}-${index}`}
                                                    testID={action.testID}
                                                    isDisabled={action.shouldDisable?.(selectedData)}
                                                />
                                                {action.renderAfter?.(selectedData) || null}
                                            </>
                                        )}
                                    </Stack>
                                ))
                            ) : props.renderNoActionsPlaceholder ? (
                                <Stack horizontal style={{ height: 42 }} align="center">
                                    {props.renderNoActionsPlaceholder()}
                                </Stack>
                            ) : null}
                        </Stack>
                        {props.headerRight}
                    </Stack>
                </PRScrollView>
                <Divider horizontal />
            </Stack>
        </CollapsableContent>
    );
};

export default React.memo(TableActions);
